import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { Button, Logo } from "@/components/ui";
import { ApiError } from "@/lib/api";
import { login } from "@/lib/endpoints";

/**
 * Pantalla de entrada.
 *
 * Pide el **negocio** además del correo porque el mismo correo puede pertenecer
 * a dos negocios distintos (un contador que lleva dos talleres).
 */
export function LoginScreen({ onSuccess }: { onSuccess: () => void }) {
  const [tenantSlug, setTenantSlug] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("tenant") || params.get("slug") || params.get("negocio");
      if (fromUrl) return fromUrl.toLowerCase().trim();
    }
    return localStorage.getItem("megasyr.lastTenant") ?? "";
  });
  const [email, setEmail] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("email") || params.get("correo");
      if (fromUrl) return fromUrl.trim();
    }
    return "";
  });
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => login({ tenant_slug: tenantSlug, email, password }),
    onSuccess: () => {
      localStorage.setItem("megasyr.lastTenant", tenantSlug);
      onSuccess();
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : "No se pudo conectar con el servidor. Revisa la red.",
      );
    },
  });

  return (
    <div className="bg-ink flex min-h-screen items-center justify-center p-4 sm:p-6">
      <form
        className="w-full max-w-sm space-y-5 rounded-msr-lg bg-ink-soft p-5 sm:p-8 text-white shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          mutation.mutate();
        }}
      >
        <div className="flex flex-col items-center gap-3">
          <Logo variant="isotipo" tone="white" className="h-16" />
          <Logo variant="wordmark" tone="white" className="h-5" alt="" />
        </div>

        <div className="space-y-3">
          <Field
            label="Negocio"
            value={tenantSlug}
            onChange={setTenantSlug}
            autoFocus
            placeholder="taller-centro"
            autoComplete="organization"
          />
          <Field
            label="Correo"
            value={email}
            onChange={setEmail}
            type="email"
            placeholder="dueno@taller.mx"
            autoComplete="username"
          />
          <Field
            label="Contraseña"
            value={password}
            onChange={setPassword}
            type="password"
            autoComplete="current-password"
          />
        </div>

        {error ? (
          <p role="alert" className="rounded-msr bg-danger/15 px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" size="lg" fullWidth loading={mutation.isPending}>
          Entrar
        </Button>

        <p className="text-center text-[11px] text-white/40">
          El POS funciona sin conexión después de la primera entrada.
        </p>

        <div className="pt-2 text-center border-t border-white/10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition-colors"
          >
            ← Volver a la página principal
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  ...rest
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoFocus?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium tracking-wide text-white/60 uppercase">
        {label}
      </span>
      <input
        {...rest}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
        className="focus:border-electric h-12 w-full rounded-msr border border-white/15 bg-white/5 px-3 text-base text-white placeholder:text-white/40 focus:outline-none"
      />
    </label>
  );
}
